import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TextField,
    Button,
    Chip,
    Collapse,
    TablePagination,
    CircularProgress,
    Alert,
    Tooltip,
    Menu,
    MenuItem,
    Popover,
    ListItemIcon,
    ListItemText,
    Divider,
    Checkbox,
    InputAdornment
} from "@mui/material";
import {
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CalendarMonth as CalendarIcon,
    AccessTime as TimeIcon,
    Edit as EditIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    Delete as DeleteIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    WarningAmber as WarningAmberIcon,
    Add as AddIcon,
    GetApp as DownloadIcon,
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
    Search as SearchIcon,
    Close as CloseIcon
} from "@mui/icons-material";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import AttendanceService from "../../../Services/attendance.service";
import AssignmentService from "../../../Services/assignment.service";
import SubAccountantService from "../../../Services/subAccountant.service";
import ClientService from "../../../Services/client.service";
import UserService from "../../../Services/user.service";
import { useTranslation } from "react-i18next";
import TipTapEditor from "../TipTapEditor/TipTapEditor";
import AuthService from "../../../Services/auth.service";
import CustomMonthDatePicker from "../CustomDates/CustomMonthDatePicker/CustomMonthDatePicker";
import { useNavigate, useLocation } from "react-router-dom";
import { useClientNavigation } from "../../../hooks/useClientNavigation";
import AssignmentDialog from "../../Assignments/SubComponents/AssignmentDialog";
import DraggableModal from "../../DraggableModal/DraggableModal";
import ManualClientTimeSection from "./ManualClientTimeSection";
const dialogStyle = {
    maxWidth: 1200,
    height: "88vh",
    width: "90%",
    borderRadius: 16,
    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.18)",
};

const formatTime = (dateTime) => {
    if (!dateTime) return "-";
    return dayjs(dateTime).format("HH:mm");
};

const formatDate = (date) => {
    return dayjs(date).format("DD/MM/YYYY");
};

const formatHours = (hours) => {
    if (hours === 0) return "-";
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    return `${wholeHours}:${minutes.toString().padStart(2, '0')}h`;
};

const stripHtmlTags = (html) => {
    if (!html) return '';
    // Create a temporary div element to parse HTML
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    // Get text content without HTML tags
    return tmp.textContent || tmp.innerText || '';
};

export default function MonthlyScheduleModal({ open, onClose, authToken, year, month, userId, userType, onDataChange }) {
    const { t, i18n } = useTranslation();
    const direction = i18n.language === 'he' || i18n.language === 'ar' ? 'rtl' : 'ltr';
    const navigate = useNavigate();
    const location = useLocation();

    // Get current client from location state or use default
    const currentClient = location?.state?.client || null;
    const clientNavigation = useClientNavigation(currentClient);
    const [scheduleData, setScheduleData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [editingRemarks, setEditingRemarks] = useState(null);
    const [tempRemarks, setTempRemarks] = useState("");
    const [editingEntry, setEditingEntry] = useState(null);
    const [tempEntryRemarks, setTempEntryRemarks] = useState("");
    const [tempCheckInTime, setTempCheckInTime] = useState("");
    const [tempCheckOutTime, setTempCheckOutTime] = useState("");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(30);
    const [currentYear, setCurrentYear] = useState(year);
    const [currentMonth, setCurrentMonth] = useState(month);
    const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
    const [sortField, setSortField] = useState('checkInTime'); // 'checkInTime' or 'checkOutTime'
    const [monthPickerAnchor, setMonthPickerAnchor] = useState(null);
    const [showAddAttendance, setShowAddAttendance] = useState(false);
    const [newAttendanceDate, setNewAttendanceDate] = useState('');
    const [newAttendanceCheckIn, setNewAttendanceCheckIn] = useState('');
    const [newAttendanceCheckOut, setNewAttendanceCheckOut] = useState('');
    const [newAttendanceRemarks, setNewAttendanceRemarks] = useState('');
    const [assignments, setAssignments] = useState([]);
    const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
    const [selectedAssignments, setSelectedAssignments] = useState([]);
    const [assignmentSearchQuery, setAssignmentSearchQuery] = useState('');
    const [assignmentStartDate, setAssignmentStartDate] = useState(null);
    const [assignmentEndDate, setAssignmentEndDate] = useState(null);
    const [loadingAssignments, setLoadingAssignments] = useState(false);
    const [linkedAssignments, setLinkedAssignments] = useState([]);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [assignmentModalOpen, setAssignmentModalOpen] = useState(false);
    const [bigIframeModalOpen, setBigIframeModalOpen] = useState(false);
    const [openedIframeUrl, setOpenedIframeUrl] = useState('');
    const [newComment, setNewComment] = useState({ content: '', attachedDocuments: [] });
    const fileInputRefUpload = React.useRef(null);
    const fileInputRefComment = React.useRef(null);
    const [subAccountants, setSubAccountants] = useState([]);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [caseIdOptions, setCaseIdOptions] = useState([]);
    const [currentUserDetails, setCurrentUserDetails] = useState(null);
    const [loadingUserDetails, setLoadingUserDetails] = useState(false);

    const getPairCount = (entries) => {
        if (!entries || !Array.isArray(entries)) return 0;
        let eventCount = 0;
        for (const entry of entries) {
            if (entry && entry.checkInTime) eventCount += 1;
            if (entry && entry.checkOutTime) eventCount += 1;
        }
        return Math.floor(eventCount / 2);
    };

    const getMissingCheckoutCount = (entries) => {
        if (!entries || !Array.isArray(entries)) return 0;

        // Count check-in records
        const checkInRecords = entries.filter(entry => entry.checkInTime && !entry.checkOutTime);

        // Count check-out records  
        const checkOutRecords = entries.filter(entry => entry.checkOutTime && !entry.checkInTime);

        // Missing checkouts = check-ins without corresponding check-outs
        return Math.max(0, checkInRecords.length - checkOutRecords.length);
    };

    const getMissingCheckinCount = (entries) => {
        if (!entries || !Array.isArray(entries)) return 0;

        // Count check-in records
        const checkInRecords = entries.filter(entry => entry.checkInTime && !entry.checkOutTime);

        // Count check-out records  
        const checkOutRecords = entries.filter(entry => entry.checkOutTime && !entry.checkInTime);

        // Missing checkins = check-outs without corresponding check-ins
        return Math.max(0, checkOutRecords.length - checkInRecords.length);
    };

    const getAttendanceIssues = (entries) => {
        if (!entries || !Array.isArray(entries)) return { missingCheckouts: 0, missingCheckins: 0, hasIssues: false };

        const checkInRecords = entries.filter(entry => entry.checkInTime && !entry.checkOutTime);
        const checkOutRecords = entries.filter(entry => entry.checkOutTime && !entry.checkInTime);

        const missingCheckouts = Math.max(0, checkInRecords.length - checkOutRecords.length);
        const missingCheckins = Math.max(0, checkOutRecords.length - checkInRecords.length);

        return {
            missingCheckouts,
            missingCheckins,
            hasIssues: missingCheckouts > 0 || missingCheckins > 0
        };
    };

    const calculateCorrectTotalHours = (entries) => {
        if (!entries || !Array.isArray(entries)) return 0;

        // Get check-in and check-out records separately
        const checkInRecords = entries.filter(entry => entry.checkInTime && !entry.checkOutTime);
        const checkOutRecords = entries.filter(entry => entry.checkOutTime && !entry.checkInTime);

        // Sort both arrays by time
        const sortedCheckIns = checkInRecords.sort((a, b) => new Date(a.checkInTime) - new Date(b.checkInTime));
        const sortedCheckOuts = checkOutRecords.sort((a, b) => new Date(a.checkOutTime) - new Date(b.checkOutTime));

        let totalHours = 0;
        const minPairs = Math.min(sortedCheckIns.length, sortedCheckOuts.length);

        // Calculate hours for complete pairs only
        for (let i = 0; i < minPairs; i++) {
            const checkInTime = new Date(sortedCheckIns[i].checkInTime);
            const checkOutTime = new Date(sortedCheckOuts[i].checkOutTime);

            // Only count if check-out is after check-in
            if (checkOutTime > checkInTime) {
                const diffMs = checkOutTime - checkInTime;
                const diffHours = diffMs / (1000 * 60 * 60); // Convert to hours
                totalHours += diffHours;
            }
        }

        return totalHours;
    };


    useEffect(() => {
        if (open) {
            loadScheduleData();
            loadAssignmentData();
            loadUserDetails();
            // Initialize assignment date range to current month
            const monthStart = dayjs().year(currentYear).month(currentMonth - 1).startOf('month');
            const monthEnd = dayjs().year(currentYear).month(currentMonth - 1).endOf('month');
            setAssignmentStartDate(monthStart.toDate());
            setAssignmentEndDate(monthEnd.toDate());
        }
    }, [open, currentYear, currentMonth, userId]);

    useEffect(() => {
        if (open) {
            setCurrentYear(year);
            setCurrentMonth(month);
        }
    }, [open, year, month]);


    const loadScheduleData = async () => {
        setLoading(true);
        try {
            const response = await AttendanceService.GetMonthlySchedule(currentYear, currentMonth, authToken, userId);
            if (response.ok) {
                const data = await response.json();
                setScheduleData(data);
            } else {
                console.error("Failed to load schedule data");
            }
        } catch (error) {
            console.error("Error loading schedule data:", error);
        } finally {
            setLoading(false);
        }
    };

    const loadUserDetails = async () => {
        if (!userId) {
            // If no userId provided, get current user from AuthService
            const currentUserId = await AuthService.getCurrentUserTypeId();
            const currentUserType = await AuthService.getCurrentUserType();

            if (currentUserId && currentUserType) {
                setLoadingUserDetails(true);
                try {
                    // Convert numeric userType to string for API
                    const userTypeString = currentUserType.toString() === '1' ? 'Client' :
                        currentUserType.toString() === '2' ? 'Accountant' :
                            currentUserType.toString() === '8' ? 'SubAccountant' : 'Client';

                    const response = await UserService.GetUserDetails(currentUserId, userTypeString, authToken);
                    if (response.ok) {
                        const userDetails = await response.json();
                        setCurrentUserDetails(userDetails);
                    }
                } catch (error) {
                    console.error("Error loading current user details:", error);
                } finally {
                    setLoadingUserDetails(false);
                }
            }
        } else {
            // If userId provided, use the provided userType or try to determine it
            setLoadingUserDetails(true);
            try {
                let userDetails = null;

                if (userType) {
                    // Use the provided userType
                    try {
                        const response = await UserService.GetUserDetails(userId, userType, authToken);
                        if (response.ok) {
                            userDetails = await response.json();
                        }
                    } catch (error) {
                        console.error(`Error loading user details with type ${userType}:`, error);
                    }
                } else {
                    // Fallback: Try different user types to find the correct one
                    const userTypes = ['Client', 'Accountant', 'SubAccountant'];

                    for (const type of userTypes) {
                        try {
                            const response = await UserService.GetUserDetails(userId, type, authToken);
                            if (response.ok) {
                                userDetails = await response.json();
                                break;
                            }
                        } catch (error) {
                            // Continue to next user type
                            continue;
                        }
                    }
                }

                if (userDetails) {
                    setCurrentUserDetails(userDetails);
                }
            } catch (error) {
                console.error("Error loading user details:", error);
            } finally {
                setLoadingUserDetails(false);
            }
        }
    };

    const loadAssignmentData = async () => {
        try {
            const accountantId = await AuthService.getCurrentUserTypeAccountantId();

            if (!accountantId) return;

            // Fetch sub-accountants (office-scoped list for assignment linking)
            const subAccountantsResponse = await SubAccountantService.GetSubAccountantByAccountantId(authToken, accountantId);
            const accountantResult = await SubAccountantService.GetAccountantById(authToken, accountantId);

            if (subAccountantsResponse && accountantResult) {
                const enhancedAccountantResult = {
                    ...accountantResult,
                    subAccountantId: accountantResult.accountantId,
                };
                const combinedSubAccountants = [enhancedAccountantResult, ...subAccountantsResponse];
                setSubAccountants(combinedSubAccountants);
            }

            // Fetch categories
            const categoriesResponse = await AssignmentService.GetAllAssignmentCategories(accountantId, authToken);
            if (categoriesResponse.ok) {
                const categoriesData = await categoriesResponse.json();
                const mappedCategories = categoriesData.map((cat) => ({
                    value: cat.id,
                    label: cat.name,
                }));
                setCategoryOptions(mappedCategories);
            }

            // Fetch clients for the whole office (by accountant), including when logged in as subaccountant
            const clientsData = await ClientService.GetMinimalClients(accountantId, authToken, 1, 1000, true);

            if (clientsData?.clients) {
                const caseOptions = [
                    { value: null, label: t('Office') },
                    ...clientsData.clients.map((client) => ({
                        value: client.caseId,
                        label: client.name,
                    })),
                ];
                setCaseIdOptions(caseOptions);
            }
        } catch (error) {
            console.error("Error loading assignment data:", error);
        }
    };

    const loadAssignments = async (startDate = null, endDate = null) => {
        setLoadingAssignments(true);
        try {
            const userId = await AuthService.getCurrentUserTypeAccountantId();
            const userType = await AuthService.getCurrentUserType();

            if (!userId) {
                setLoadingAssignments(false);
                return;
            }

            // Use provided dates or default to assignment date range state
            const effectiveStartDate = startDate || assignmentStartDate;
            const effectiveEndDate = endDate || assignmentEndDate;

            if (!effectiveStartDate || !effectiveEndDate) {
                setLoadingAssignments(false);
                return;
            }

            const response = await AssignmentService.GetAssignmentsByAccountantId(
                userId,
                authToken,
                dayjs(effectiveStartDate),
                dayjs(effectiveEndDate)
            );

            if (response.ok) {
                const data = await response.json();
                setAssignments(data);
            } else {
                console.error("Failed to load assignments");
                Swal.fire({
                    title: t('Error') || 'Error',
                    text: t('FailedToLoadAssignments') || 'Failed to load assignments.',
                    icon: 'error',
                    confirmButtonText: t('OK') || 'OK'
                });
            }
        } catch (error) {
            console.error("Error loading assignments:", error);
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('ErrorLoadingAssignments') || 'Error loading assignments.',
                icon: 'error',
                confirmButtonText: t('OK') || 'OK'
            });
        } finally {
            setLoadingAssignments(false);
        }
    };

    const toggleRowExpansion = (day) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(day)) {
            newExpanded.delete(day);
        } else {
            newExpanded.add(day);
        }
        setExpandedRows(newExpanded);
    };

    const startEditingRemarks = (day, currentRemarks) => {
        setEditingRemarks(day);
        setTempRemarks(currentRemarks || "");
        const dayData = scheduleData.find(item => item.day === day);
        setLinkedAssignments(dayData?.linkedAssignments || []);
    };

    const handleOpenAssignmentDialog = () => {
        setAssignmentDialogOpen(true);
        // Load assignments with current date range when dialog opens
        if (assignmentStartDate && assignmentEndDate) {
            loadAssignments(assignmentStartDate, assignmentEndDate);
        }
    };

    const handleCloseAssignmentDialog = () => {
        setAssignmentDialogOpen(false);
        setSelectedAssignments([]);
        setAssignmentSearchQuery('');
    };

    const handleLoadAssignmentsWithDateRange = () => {
        if (!assignmentStartDate || !assignmentEndDate) {
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('PleaseSelectDateRange') || 'Please select both start and end dates.',
                icon: 'warning',
                confirmButtonText: t('OK') || 'OK'
            });
            return;
        }

        if (dayjs(assignmentEndDate).isBefore(dayjs(assignmentStartDate))) {
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('EndDateMustBeAfterStartDate') || 'End date must be after start date.',
                icon: 'warning',
                confirmButtonText: t('OK') || 'OK'
            });
            return;
        }

        loadAssignments(assignmentStartDate, assignmentEndDate);
    };

    const handleSelectAssignment = (assignment) => {
        setSelectedAssignments(prev => {
            const isSelected = prev.find(a => a.id === assignment.id);
            if (isSelected) {
                return prev.filter(a => a.id !== assignment.id);
            } else {
                return [...prev, assignment];
            }
        });
    };

    const handleInsertAssignments = () => {
        if (selectedAssignments.length === 0) {
            Swal.fire({
                title: t('NoAssignmentsSelected') || 'No Assignments Selected',
                text: t('PleaseSelectAssignments') || 'Please select at least one assignment to insert.',
                icon: 'warning',
                confirmButtonText: t('OK') || 'OK'
            });
            return;
        }

        // Add selected assignments to linked assignments
        setLinkedAssignments(prev => {
            const newAssignments = selectedAssignments.filter(assignment =>
                !prev.some(existing => existing.id === assignment.id)
            );
            return [...prev, ...newAssignments];
        });

        // Clear selected assignments
        setSelectedAssignments([]);
        handleCloseAssignmentDialog();
    };


    const handleAssignmentClick = async (assignmentId) => {
        try {
            // Fetch the assignment by ID
            const response = await AssignmentService.GetAssignmentById(assignmentId, authToken);
            if (response.ok) {
                const assignment = await response.json();
                setSelectedAssignment(assignment);
                setAssignmentModalOpen(true);
            } else {
                Swal.fire({
                    title: t('Error') || 'Error',
                    text: t('FailedToLoadAssignment') || 'Failed to load assignment',
                    icon: 'error',
                    confirmButtonText: t('OK') || 'OK'
                });
            }
        } catch (error) {
            console.error('Error fetching assignment:', error);
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('FailedToLoadAssignment') || 'Failed to load assignment',
                icon: 'error',
                confirmButtonText: t('OK') || 'OK'
            });
        }
    };

    const handleCloseAssignmentModal = () => {
        setAssignmentModalOpen(false);
        setSelectedAssignment(null);
        setNewComment({ content: '', attachedDocuments: [] });
    };

    const handleRemoveLinkedAssignment = (assignmentId) => {
        setLinkedAssignments(prev => prev.filter(assignment => assignment.id !== assignmentId));
    };

    // Assignment Dialog handlers
    const handleOpenModal = (url) => {
        setOpenedIframeUrl(url);
        setBigIframeModalOpen(true);
    };

    const handleCloseModal = () => {
        setBigIframeModalOpen(false);
        setOpenedIframeUrl('');
    };

    const handleChangeNewCommentContent = (content) => {
        setNewComment((prev) => ({ ...prev, content }));
    };

    const handleChangeCurrentDescription = (description) => {
        setSelectedAssignment((prev) => ({ ...prev, description }));
    };

    const handleUploadClick = () => {
        if (fileInputRefUpload.current) {
            fileInputRefUpload.current.value = "";
            fileInputRefUpload.current.click();
        }
    };

    const handleUploadClickComment = () => {
        if (fileInputRefComment.current) {
            fileInputRefComment.current.value = "";
            fileInputRefComment.current.click();
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedAssignment((prev) => ({
                ...prev,
                attachedDocuments: [...(prev.attachedDocuments || []), file],
                newAttachedDocuments: [...(prev.newAttachedDocuments || []), file],
            }));
        }
    };

    const handleCommentFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setNewComment((prev) => ({
                ...prev,
                attachedDocuments: [...(prev.attachedDocuments || []), file],
            }));
        }
    };

    const handleDropDocumentToAssignment = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) {
            setSelectedAssignment((prev) => ({
                ...prev,
                attachedDocuments: [...(prev.attachedDocuments || []), file],
                newAttachedDocuments: [...(prev.newAttachedDocuments || []), file],
            }));
        }
    };

    const handleRemoveDocumentFromAssignment = (indexToRemove) => {
        setSelectedAssignment((prev) => ({
            ...prev,
            attachedDocuments: prev.attachedDocuments.filter((_, index) => index !== indexToRemove),
            newAttachedDocuments: prev.newAttachedDocuments?.filter((_, index) => index !== indexToRemove),
        }));
    };

    const handleDropDocumentToComment = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) {
            setNewComment((prev) => ({
                ...prev,
                attachedDocuments: [...(prev.attachedDocuments || []), file],
            }));
        }
    };

    const handleRemoveDocumentFromComment = (indexToRemove) => {
        setNewComment((prev) => ({
            ...prev,
            attachedDocuments: prev.attachedDocuments.filter((_, index) => index !== indexToRemove),
        }));
    };

    const formatDateWithHour = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return dayjs(date).format("DD-MM-YYYY HH:mm");
    };

    const getPriorityEmoji = (priority) => {
        const priorityEmojis = {
            0: '🔴', // High
            1: '🟡', // Medium  
            2: '🟢'  // Low
        };
        return priorityEmojis[priority] || '⚪';
    };

    const filteredAssignments = assignments.filter(assignment =>
        assignment.summary.toLowerCase().includes(assignmentSearchQuery.toLowerCase()) ||
        assignment.description.toLowerCase().includes(assignmentSearchQuery.toLowerCase())
    );

    const cancelEditingRemarks = () => {
        setEditingRemarks(null);
        setTempRemarks("");
    };

    const saveRemarks = async (day) => {
        try {
            // Save remarks
            const response = await AttendanceService.UpdateDayRemarks(
                year, month, day, tempRemarks, authToken, userId
            );
            if (response.ok) {
                // Save linked assignments if any
                if (linkedAssignments.length > 0) {
                    await saveLinkedAssignments(day);
                }

                // Update local data
                setScheduleData(prev => prev.map(item =>
                    item.day === day ? {
                        ...item,
                        remarks: tempRemarks,
                        linkedAssignments: linkedAssignments
                    } : item
                ));
                setEditingRemarks(null);
                setTempRemarks("");
                setLinkedAssignments([]); // Clear linked assignments after saving
            } else {
                console.error("Failed to update remarks");
            }
        } catch (error) {
            console.error("Error updating remarks:", error);
        }
    };

    const saveLinkedAssignments = async (day) => {
        try {
            const response = await AttendanceService.SaveLinkedAssignments(
                year, month, day, linkedAssignments, authToken
            );
            if (!response.ok) {
                console.error("Failed to save linked assignments");
            }
        } catch (error) {
            console.error("Error saving linked assignments:", error);
        }
    };

    const startEditingEntry = (entry) => {
        setEditingEntry(entry.id);
        setTempEntryRemarks(entry.remarks || "");
        setTempCheckInTime(entry.checkInTime ? dayjs(entry.checkInTime).format("HH:mm") : "");
        setTempCheckOutTime(entry.checkOutTime ? dayjs(entry.checkOutTime).format("HH:mm") : "");

        // Find the day this entry belongs to and expand it
        const dayData = scheduleData.find(day => day.entries.some(e => e.id === entry.id));
        if (dayData && !expandedRows.has(dayData.day)) {
            setExpandedRows(prev => new Set([...prev, dayData.day]));
        }
    };

    const cancelEditingEntry = () => {
        setEditingEntry(null);
        setTempEntryRemarks("");
        setTempCheckInTime("");
        setTempCheckOutTime("");
    };

    const validateAttendancePair = (checkInTime, checkOutTime) => {
        if (checkInTime && checkOutTime) {
            const checkIn = dayjs(checkInTime);
            const checkOut = dayjs(checkOutTime);

            if (checkOut.isBefore(checkIn) || checkOut.isSame(checkIn)) {
                return {
                    isValid: false,
                    message: t('CheckOutMustBeAfterCheckIn') || 'Check-out time must be after check-in time'
                };
            }
        }
        return { isValid: true };
    };

    const saveEntry = async (entryId) => {
        try {
            // Convert time strings back to full datetime
            const dayData = scheduleData.find(day => day.entries.some(entry => entry.id === entryId));

            if (dayData) {
                // Find the original entry to get the base date
                const originalEntry = dayData.entries.find(entry => entry.id === entryId);
                if (!originalEntry) return;
                
                // Create new times by directly modifying the original times if they exist, or creating new ones
                let newCheckInTime = null;
                let newCheckOutTime = null;
                
                if (tempCheckInTime && originalEntry.checkInTime) {
                    // Use the original check-in time as base and just change the time part, treating as UTC
                    const originalCheckIn = dayjs(originalEntry.checkInTime).utc();
                    newCheckInTime = originalCheckIn.hour(parseInt(tempCheckInTime.split(':')[0])).minute(parseInt(tempCheckInTime.split(':')[1])).second(0).millisecond(0).toISOString();
                } else if (tempCheckInTime) {
                    // Create new check-in time using dayData.date, treating as UTC
                    const baseDate = dayjs(dayData.date).utc();
                    newCheckInTime = baseDate.hour(parseInt(tempCheckInTime.split(':')[0])).minute(parseInt(tempCheckInTime.split(':')[1])).second(0).millisecond(0).toISOString();
                }
                
                if (tempCheckOutTime && originalEntry.checkOutTime) {
                    // Use the original check-out time as base and just change the time part, treating as UTC
                    const originalCheckOut = dayjs(originalEntry.checkOutTime).utc();
                    newCheckOutTime = originalCheckOut.hour(parseInt(tempCheckOutTime.split(':')[0])).minute(parseInt(tempCheckOutTime.split(':')[1])).second(0).millisecond(0).toISOString();
                } else if (tempCheckOutTime) {
                    // Create new check-out time using dayData.date, treating as UTC
                    const baseDate = dayjs(dayData.date).utc();
                    newCheckOutTime = baseDate.hour(parseInt(tempCheckOutTime.split(':')[0])).minute(parseInt(tempCheckOutTime.split(':')[1])).second(0).millisecond(0).toISOString();
                }
                
                // AUTOMATIC SPLIT: If both check-in and check-out times are provided, split into two entries
                if (newCheckInTime && newCheckOutTime) {
                    // Validate the pair first
                    const validation = validateAttendancePair(newCheckInTime, newCheckOutTime);
                    if (!validation.isValid) {
                        Swal.fire({
                            title: t('ValidationError') || 'Validation Error',
                            text: validation.message,
                            icon: 'warning',
                            confirmButtonText: t('OK') || 'OK'
                        });
                        return;
                    }

                    // Delete the original entry
                    const deleteResponse = await AttendanceService.DeleteAttendanceEntry(entryId, authToken);
                    
                    if (!deleteResponse.ok) {
                        Swal.fire({
                            title: t('Error') || 'Error',
                            text: t('FailedToUpdateEntry') || 'Failed to update entry.',
                            icon: 'error',
                            confirmButtonText: t('OK') || 'OK'
                        });
                        return;
                    }

                    // Create two separate entries
                    const entryDate = dayjs(dayData.date);
                    const createResponse = await AttendanceService.CreateAttendance({
                        userId: originalEntry.userId || (userId || await AuthService.getCurrentUserTypeId()),
                        userType: originalEntry.userType || (userType || await AuthService.getCurrentUserType()),
                        year: entryDate.year(),
                        month: entryDate.month() + 1,
                        day: entryDate.date(),
                        checkInTime: newCheckInTime,
                        checkOutTime: newCheckOutTime,
                        remarks: tempEntryRemarks
                    }, authToken);

                    if (createResponse.ok) {
                        setEditingEntry(null);
                        setTempEntryRemarks("");
                        setTempCheckInTime("");
                        setTempCheckOutTime("");

                        // Refresh parent component data
                        if (onDataChange) {
                            onDataChange();
                        }
                        
                        // Refetch the schedule data to ensure UI is up to date
                        await loadScheduleData();

                        // Show success message explaining the split
                        Swal.fire({
                            title: t('Success') || 'Success!',
                            text: t('EntrySplitSuccessfully') || 'Entry split into two separate records: one for check-in and one for check-out.',
                            icon: 'success',
                            timer: 3000,
                            showConfirmButton: false
                        });
                    } else {
                        Swal.fire({
                            title: t('Error') || 'Error',
                            text: t('FailedToCreateEntries') || 'Failed to create separate entries.',
                            icon: 'error',
                            confirmButtonText: t('OK') || 'OK'
                        });
                    }
                    return;
                }

                // Single time update - proceed normally
                // Make API call to update the entry in the database
                const response = await AttendanceService.UpdateAttendanceEntry(
                    entryId,
                    newCheckInTime,
                    newCheckOutTime,
                    tempEntryRemarks,
                    authToken
                );

                if (response.ok) {
                    setEditingEntry(null);
                    setTempEntryRemarks("");
                    setTempCheckInTime("");
                    setTempCheckOutTime("");

                    // Refresh parent component data
                    if (onDataChange) {
                        onDataChange();
                    }
                    
                    // Refetch the schedule data to ensure UI is up to date
                    await loadScheduleData();

                    // Show success message
                    Swal.fire({
                        title: t('Success') || 'Success!',
                        text: t('EntryUpdatedSuccessfully') || 'Entry updated successfully.',
                        icon: 'success',
                        timer: 2000,
                        showConfirmButton: false
                    });
                } else {
                    Swal.fire({
                        title: t('Error') || 'Error',
                        text: t('FailedToUpdateEntry') || 'Failed to update entry.',
                        icon: 'error',
                        confirmButtonText: t('OK') || 'OK'
                    });
                }
            }
        } catch (error) {
            console.error("Error updating entry:", error);
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('ErrorUpdatingEntry') || 'Error updating entry.',
                icon: 'error',
                confirmButtonText: t('OK') || 'OK'
            });
        }
    };

    const handleDeleteEntry = async (entryId) => {
        const result = await Swal.fire({
            title: t('ConfirmDeleteEntry') || 'Are you sure?',
            text: t('DeleteEntryWarning') || 'This will permanently delete this attendance entry.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: t('Delete') || 'Delete',
            cancelButtonText: t('Cancel') || 'Cancel',
            reverseButtons: direction === 'rtl'
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            const response = await AttendanceService.DeleteAttendanceEntry(entryId, authToken);

            if (response.ok) {
                // Remove the entry from local state
                setScheduleData(prev => prev.map(dayData => ({
                    ...dayData,
                    entries: dayData.entries.filter(entry => entry.id !== entryId)
                })).filter(dayData => dayData.entries.length > 0)); // Remove days with no entries

                // Refresh data to get updated totals
                await loadScheduleData();

                // Refresh parent component data
                if (onDataChange) {
                    onDataChange();
                }

                // Show success message
                Swal.fire({
                    title: t('Success') || 'Success!',
                    text: t('EntryDeletedSuccessfully') || 'Entry deleted successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                Swal.fire({
                    title: t('Error') || 'Error',
                    text: t('FailedToDeleteEntry') || 'Failed to delete entry. Please try again.',
                    icon: 'error',
                    confirmButtonText: t('OK') || 'OK'
                });
            }
        } catch (error) {
            console.error('Error deleting entry:', error);
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('ErrorDeletingEntry') || 'Error deleting entry. Please try again.',
                icon: 'error',
                confirmButtonText: t('OK') || 'OK'
            });
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const navigateMonth = (direction) => {
        const currentDate = dayjs().year(currentYear).month(currentMonth - 1);
        const newDate = direction === 'prev' ? currentDate.subtract(1, 'month') : currentDate.add(1, 'month');

        setCurrentYear(newDate.year());
        setCurrentMonth(newDate.month() + 1);
        setPage(0);
        setExpandedRows(new Set());
        setEditingRemarks(null);
        setEditingEntry(null);
    };

    const isCurrentMonthEditable = () => {
        const currentDate = dayjs();
        const displayedDate = dayjs().year(currentYear).month(currentMonth - 1);
        return displayedDate.isSame(currentDate, 'month');
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const sortEntries = (entries) => {
        return [...entries].sort((a, b) => {
            // Get the earliest time for each entry (either checkIn or checkOut, whichever exists and is earlier)
            const getEarliestTime = (entry) => {
                const checkIn = entry.checkInTime ? dayjs(entry.checkInTime) : null;
                const checkOut = entry.checkOutTime ? dayjs(entry.checkOutTime) : null;

                if (!checkIn && !checkOut) return dayjs(0); // No times
                if (!checkIn) return checkOut;
                if (!checkOut) return checkIn;

                // Return the earlier of the two times
                return checkIn.isBefore(checkOut) ? checkIn : checkOut;
            };

            const aTime = getEarliestTime(a);
            const bTime = getEarliestTime(b);

            if (sortOrder === 'asc') {
                return aTime.isBefore(bTime) ? -1 : aTime.isAfter(bTime) ? 1 : 0;
            } else {
                return aTime.isAfter(bTime) ? -1 : aTime.isBefore(bTime) ? 1 : 0;
            }
        });
    };

    const paginatedData = scheduleData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const handleMonthPickerOpen = (event) => {
        setMonthPickerAnchor(event.currentTarget);
    };

    const handleMonthPickerClose = () => {
        setMonthPickerAnchor(null);
    };

    const handleMonthSelect = (selectedYear, selectedMonth) => {
        setCurrentYear(selectedYear);
        setCurrentMonth(selectedMonth);
        setPage(0);
        setExpandedRows(new Set());
        setEditingRemarks(null);
        setEditingEntry(null);
        handleMonthPickerClose();
    };

    const handleAddAttendance = () => {
        setShowAddAttendance(true);
    };

    const handleSaveNewAttendance = async () => {
        try {
            if (!newAttendanceDate) {
                Swal.fire({
                    title: t('Error') || 'Error',
                    text: t('PleaseSelectDate') || 'Please select a date',
                    icon: 'warning',
                    confirmButtonText: t('OK') || 'OK'
                });
                return;
            }

            // Parse date components and create UTC date to prevent timezone conversion issues
            const dateParts = newAttendanceDate.split('-'); // Format: YYYY-MM-DD
            const baseDate = dayjs.utc().year(parseInt(dateParts[0])).month(parseInt(dateParts[1]) - 1).date(parseInt(dateParts[2])).hour(0).minute(0).second(0).millisecond(0);
            
            const checkInTime = newAttendanceCheckIn ?
                baseDate.hour(parseInt(newAttendanceCheckIn.split(':')[0])).minute(parseInt(newAttendanceCheckIn.split(':')[1])).second(0).millisecond(0).toISOString() : null;
            const checkOutTime = newAttendanceCheckOut ?
                baseDate.hour(parseInt(newAttendanceCheckOut.split(':')[0])).minute(parseInt(newAttendanceCheckOut.split(':')[1])).second(0).millisecond(0).toISOString() : null;

            // Determine target user ID and type
            let targetUserId, targetUserType;

            if (userId) {
                // If userId prop is provided, use it (accountant managing client/subaccountant)
                targetUserId = userId;
                // Get user type from currentUserDetails or try to determine it
                if (currentUserDetails && currentUserDetails.userType) {
                    const userTypeMap = {
                        'Client': 1,
                        'Accountant': 2,
                        'SubAccountant': 8
                    };
                    targetUserType = userTypeMap[currentUserDetails.userType] || 1;
                } else {
                    // Default to Client if we can't determine
                    targetUserType = 1;
                }
            } else {
                // If no userId prop, use current authenticated user
                targetUserId = await AuthService.getCurrentUserTypeId();
                targetUserType = await AuthService.getCurrentUserType();
            }

            if (!targetUserId) {
                Swal.fire({
                    title: t('Error') || 'Error',
                    text: t('UserIdNotFound') || 'User ID not found. Please log in again.',
                    icon: 'error',
                    confirmButtonText: t('OK') || 'OK'
                });
                return;
            }

            if (!targetUserType) {
                Swal.fire({
                    title: t('Error') || 'Error',
                    text: t('UserTypeNotFound') || 'User type not found. Please log in again.',
                    icon: 'error',
                    confirmButtonText: t('OK') || 'OK'
                });
                return;
            }

            // Create attendance record
            const response = await AttendanceService.CreateAttendance({
                userId: targetUserId,
                userType: targetUserType,
                year: baseDate.year(),
                month: baseDate.month() + 1,
                day: baseDate.date(),
                checkInTime,
                checkOutTime,
                remarks: newAttendanceRemarks
            }, authToken);

            if (response.ok) {
                // Refresh data
                await loadScheduleData();
                setShowAddAttendance(false);
                setNewAttendanceDate('');
                setNewAttendanceCheckIn('');
                setNewAttendanceCheckOut('');
                setNewAttendanceRemarks('');

                // Refresh parent component data
                if (onDataChange) {
                    onDataChange();
                }

                // Show success message
                Swal.fire({
                    title: t('Success') || 'Success!',
                    text: t('AttendanceCreatedSuccessfully') || 'Attendance record created successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
            } else {
                Swal.fire({
                    title: t('Error') || 'Error',
                    text: t('FailedToCreateAttendance') || 'Failed to create attendance record.',
                    icon: 'error',
                    confirmButtonText: t('OK') || 'OK'
                });
            }
        } catch (error) {
            console.error('Error creating attendance record:', error);
            Swal.fire({
                title: t('Error') || 'Error',
                text: t('ErrorCreatingAttendance') || 'Error creating attendance record.',
                icon: 'error',
                confirmButtonText: t('OK') || 'OK'
            });
        }
    };

    const handleExportPdf = async () => {
        try {
            const accountantId = await AuthService.getCurrentUserTypeId();
            const targetUserId = userId || await AuthService.getCurrentUserTypeId();
            const filter = {
                userIds: userId ? [userId] : [],
                accountantId: accountantId || '',
                startUtc: new Date(currentYear, currentMonth - 1, 1),
                endUtc: new Date(currentYear, currentMonth, 0)
            };

            const response = await AttendanceService.ExportDetailedPdfReport(
                authToken,
                filter,
                accountantId,
                currentYear,
                currentMonth
            );

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const fileName = userId
                    ? `דוח_נוכחות_מפורט_${currentUserDetails?.name || targetUserId}_${currentYear}_${currentMonth}.pdf`
                    : `דוח_נוכחות_מפורט_${currentYear}_${currentMonth}.pdf`;
                link.href = url;
                link.download = fileName;
                link.click();
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to generate PDF report');
            }
        } catch (error) {
            console.error('Error generating PDF report:', error);
        }
    };

    const handleExportExcel = async () => {
        try {
            const accountantId = await AuthService.getCurrentUserTypeId();
            const targetUserId = userId || await AuthService.getCurrentUserTypeId();
            const filter = {
                userIds: userId ? [userId] : [],
                accountantId: accountantId || '',
                startUtc: new Date(currentYear, currentMonth - 1, 1),
                endUtc: new Date(currentYear, currentMonth, 0)
            };

            const response = await AttendanceService.ExportExcelReport(
                authToken,
                filter,
                accountantId,
                currentYear,
                currentMonth
            );

            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                const fileName = userId
                    ? `דוח_נוכחות_${currentUserDetails?.name || targetUserId}_${currentYear}_${currentMonth}.xlsx`
                    : `דוח_נוכחות_${currentYear}_${currentMonth}.xlsx`;
                link.href = url;
                link.download = fileName;
                link.click();
                window.URL.revokeObjectURL(url);
            } else {
                console.error('Failed to generate Excel report');
            }
        } catch (error) {
            console.error('Error generating Excel report:', error);
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                dir={direction}
                PaperProps={{
                    sx: {
                        ...dialogStyle,
                        borderRadius: 3,
                        overflow: 'hidden',
                        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                        backdropFilter: 'saturate(110%) blur(6px)'
                    }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    py: 2.5,
                    px: 3,
                    borderBottom: '1px solid rgba(255,255,255,0.15)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    {/* Background Pattern */}
                    <Box sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.05) 0%, transparent 50%)',
                        pointerEvents: 'none'
                    }} />

                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        position: 'relative',
                        zIndex: 1
                    }}>
                        {/* Left Section: Navigation + Title */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            flex: 1,
                            flexDirection: direction === 'rtl' ? 'row-reverse' : 'row'
                        }}>
                            {/* Navigation Arrows */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                            }}>
                                <IconButton
                                    onClick={() => navigateMonth('prev')}
                                    sx={{
                                        color: 'white',
                                        backgroundColor: 'rgba(255,255,255,0.12)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.25)',
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                    size="small"
                                >
                                    {direction === 'rtl' ? <ChevronRightIcon /> : <ChevronLeftIcon />}
                                </IconButton>
                                <IconButton
                                    onClick={() => navigateMonth('next')}
                                    sx={{
                                        color: 'white',
                                        backgroundColor: 'rgba(255,255,255,0.12)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255,255,255,0.25)',
                                            transform: 'scale(1.05)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                    size="small"
                                >
                                    {direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                                </IconButton>
                            </Box>

                            {/* Calendar Icon + Title */}
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                cursor: 'pointer',
                                padding: 1,
                                borderRadius: 2,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.1)',
                                    transform: 'translateY(-1px)'
                                }
                            }}
                                onClick={handleMonthPickerOpen}>
                                <CalendarIcon sx={{
                                    fontSize: 32,
                                    color: 'white',
                                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))'
                                }} />
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: direction === 'rtl' ? 'flex-end' : 'flex-start',
                                    gap: 0.5
                                }}>
                                    <Typography variant="h4" component="h2" sx={{
                                        fontWeight: 700,
                                        fontSize: '1.75rem',
                                        textAlign: direction === 'rtl' ? 'right' : 'left',
                                        textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        letterSpacing: '0.5px'
                                    }}>
                                        {t('MonthlySchedule') || 'Monthly Schedule'}
                                    </Typography>
                                    <Typography variant="h6" sx={{
                                        fontWeight: 500,
                                        fontSize: '1.1rem',
                                        color: 'rgba(255,255,255,0.9)',
                                        textAlign: direction === 'rtl' ? 'right' : 'left',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                    }}>
                                        {dayjs().year(currentYear).month(currentMonth - 1).format('MMMM YYYY')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                        {/* Center Section: User Details */}
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 0.5,
                            flex: 1,
                            px: 2
                        }}>
                            {loadingUserDetails ? (
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    py: 1
                                }}>
                                    <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.8)' }} />
                                    <Typography variant="body2" sx={{
                                        color: 'rgba(255,255,255,0.8)',
                                        fontSize: '0.9rem',
                                        fontWeight: 500
                                    }}>
                                        {t('LoadingUserDetails') || 'Loading user details...'}
                                    </Typography>
                                </Box>
                            ) : currentUserDetails ? (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 0.25,
                                    textAlign: 'center'
                                }}>
                                    <Typography variant="h6" sx={{
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                        color: 'white',
                                        textShadow: '0 1px 2px rgba(0,0,0,0.3)'
                                    }}>
                                        {currentUserDetails.name}
                                    </Typography>
                                    <Typography variant="body2" sx={{
                                        color: 'rgba(255,255,255,0.85)',
                                        fontSize: '0.85rem',
                                        fontWeight: 500
                                    }}>
                                        {t(currentUserDetails.userType) || currentUserDetails.userType} • {t('ID') || 'ID'}: {currentUserDetails.userId}
                                    </Typography>
                                </Box>
                            ) : null}
                        </Box>

                        {/* Right Section: Action Buttons */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexDirection: direction === 'rtl' ? 'row-reverse' : 'row'
                        }}>
                            <Tooltip title={t('AddAttendance') || 'Add Attendance'} arrow>
                                <IconButton
                                    onClick={handleAddAttendance}
                                    sx={{
                                        color: 'white',
                                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(76, 175, 80, 0.3)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 175, 80, 0.4)',
                                            transform: 'scale(1.1)',
                                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                    size="small"
                                >
                                    <AddIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={t('ExportPDF') || 'Export PDF'} arrow>
                                <IconButton
                                    onClick={handleExportPdf}
                                    sx={{
                                        color: 'white',
                                        backgroundColor: 'rgba(244, 67, 54, 0.2)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(244, 67, 54, 0.3)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(244, 67, 54, 0.4)',
                                            transform: 'scale(1.1)',
                                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                    size="small"
                                >
                                    <PdfIcon />
                                </IconButton>
                            </Tooltip>

                            <Tooltip title={t('ExportExcel') || 'Export Excel'} arrow>
                                <IconButton
                                    onClick={handleExportExcel}
                                    sx={{
                                        color: 'white',
                                        backgroundColor: 'rgba(76, 175, 80, 0.2)',
                                        backdropFilter: 'blur(10px)',
                                        border: '1px solid rgba(76, 175, 80, 0.3)',
                                        '&:hover': {
                                            backgroundColor: 'rgba(76, 175, 80, 0.4)',
                                            transform: 'scale(1.1)',
                                            boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                                        },
                                        transition: 'all 0.2s ease'
                                    }}
                                    size="small"
                                >
                                    <ExcelIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </DialogTitle>

                {/* Month Picker Popover */}
                <Popover
                    open={Boolean(monthPickerAnchor)}
                    anchorEl={monthPickerAnchor}
                    onClose={handleMonthPickerClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: direction === 'rtl' ? 'right' : 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: direction === 'rtl' ? 'right' : 'left',
                    }}
                >
                    <Box sx={{
                        p: 2,
                        minWidth: 300,
                        direction: direction,
                        textAlign: direction === 'rtl' ? 'right' : 'left'
                    }}>
                        <Typography variant="h6" sx={{
                            mb: 2,
                            textAlign: direction === 'rtl' ? 'left' : 'right'
                        }}>
                            {t('SelectMonth') || 'Select Month'}
                        </Typography>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                            alignItems: direction === 'rtl' ? 'flex-end' : 'flex-start'
                        }}>
                            {Array.from({ length: 12 }, (_, i) => i + 1).map(monthNum => (
                                <Button
                                    key={monthNum}
                                    variant={currentMonth === monthNum ? 'contained' : 'text'}
                                    onClick={() => handleMonthSelect(currentYear, monthNum)}
                                    sx={{
                                        justifyContent: direction === 'rtl' ? 'flex-end' : 'flex-start',
                                        width: '100%',
                                        textAlign: direction === 'rtl' ? 'right' : 'left'
                                    }}
                                >
                                    {dayjs().month(monthNum - 1).format('MMMM')}
                                </Button>
                            ))}
                        </Box>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{
                            display: 'flex',
                            gap: 1,
                            flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
                            alignItems: 'center'
                        }}>
                            <TextField
                                type="number"
                                label={t('Year') || 'Year'}
                                value={currentYear}
                                onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                                size="small"
                                sx={{
                                    width: 100,
                                    '& input': {
                                        textAlign: direction === 'rtl' ? 'right' : 'left'
                                    }
                                }}
                            />
                            <Button
                                variant="contained"
                                onClick={() => handleMonthSelect(currentYear, currentMonth)}
                                size="small"
                            >
                                {t('Apply') || 'Apply'}
                            </Button>
                        </Box>
                    </Box>
                </Popover>

                {/* Add Attendance Dialog */}
                <Dialog
                    open={showAddAttendance}
                    onClose={() => setShowAddAttendance(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        {t('AddAttendance') || 'Add Attendance'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            <TextField
                                label={t('Date') || 'Date'}
                                type="date"
                                value={newAttendanceDate}
                                onChange={(e) => setNewAttendanceDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                label={t('CheckInTime') || 'Check In Time'}
                                type="time"
                                value={newAttendanceCheckIn}
                                onChange={(e) => setNewAttendanceCheckIn(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <TextField
                                label={t('CheckOutTime') || 'Check Out Time'}
                                type="time"
                                value={newAttendanceCheckOut}
                                onChange={(e) => setNewAttendanceCheckOut(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                            <Box sx={{ minWidth: 400, maxWidth: 500 }}>
                                <TipTapEditor
                                    value={newAttendanceRemarks}
                                    onChange={setNewAttendanceRemarks}
                                    placeholder={t('EnterRemarks') || 'Enter remarks...'}
                                    direction={direction}
                                />
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowAddAttendance(false)}>
                            {t('Cancel') || 'Cancel'}
                        </Button>
                        <Button onClick={handleSaveNewAttendance} variant="contained">
                            {t('Save') || 'Save'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Assignment Selection Dialog */}
                <Dialog
                    open={assignmentDialogOpen}
                    onClose={handleCloseAssignmentDialog}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ textAlign: 'center' }}>
                        {t('SelectAssignments') || 'Select Assignments'}
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                            {/* Date Range Selector */}
                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                alignItems: 'center',
                                p: 2,
                                backgroundColor: 'rgba(102, 126, 234, 0.05)',
                                borderRadius: 2,
                                border: '1px solid rgba(102, 126, 234, 0.2)'
                            }}>

                                <Box sx={{ flex: 1 }}>
                                    <CustomMonthDatePicker
                                        date={assignmentEndDate}
                                        onDateChange={(newDate) => {
                                            // Set to last day of the selected month
                                            const lastDay = new Date(newDate.getFullYear(), newDate.getMonth() + 1, 0);
                                            setAssignmentEndDate(lastDay);
                                        }}
                                        label={t('EndMonth') || 'End Month'}
                                        height="40px"
                                        width="100%"
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <CustomMonthDatePicker
                                        date={assignmentStartDate}
                                        onDateChange={(newDate) => setAssignmentStartDate(newDate)}
                                        label={t('StartMonth') || 'Start Month'}
                                        height="40px"
                                        width="100%"
                                    />
                                </Box>
                                <Button
                                    variant="contained"
                                    onClick={handleLoadAssignmentsWithDateRange}
                                    disabled={loadingAssignments}
                                    sx={{
                                        textTransform: 'none',
                                        minWidth: '120px',
                                        height: '40px'
                                    }}
                                >
                                    {loadingAssignments ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        t('LoadAssignments') || 'Load Assignments'
                                    )}
                                </Button>
                            </Box>

                            {/* Search Bar */}
                            <TextField
                                label={t('SearchAssignments') || 'Search Assignments'}
                                value={assignmentSearchQuery}
                                onChange={(e) => setAssignmentSearchQuery(e.target.value)}
                                fullWidth
                                size="small"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {/* Assignment Count */}
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                px: 1
                            }}>
                                <Typography variant="body2" color="text.secondary">
                                    {t('TotalAssignments') || 'Total Assignments'}: {assignments.length}
                                </Typography>
                                {assignmentSearchQuery && (
                                    <Typography variant="body2" color="text.secondary">
                                        {t('FilteredResults') || 'Filtered'}: {filteredAssignments.length}
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{
                                maxHeight: 400,
                                overflow: 'auto',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                p: 1
                            }}>
                                {loadingAssignments ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : filteredAssignments.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                                        {assignments.length === 0
                                            ? (t('NoAssignmentsInDateRange') || 'No assignments found in the selected date range. Please adjust the dates and click "Load Assignments".')
                                            : (t('NoAssignmentsFound') || 'No assignments found')
                                        }
                                    </Typography>
                                ) : (
                                    filteredAssignments.map((assignment) => (
                                        <Box
                                            key={assignment.id}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 1,
                                                mb: 1,
                                                border: '1px solid #e0e0e0',
                                                borderRadius: 1,
                                                cursor: 'pointer',
                                                backgroundColor: selectedAssignments.find(a => a.id === assignment.id)
                                                    ? 'rgba(102, 126, 234, 0.1)'
                                                    : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(102, 126, 234, 0.05)'
                                                }
                                            }}
                                            onClick={() => handleSelectAssignment(assignment)}
                                            dir={direction}
                                        >
                                            <Checkbox
                                                checked={selectedAssignments.find(a => a.id === assignment.id) !== undefined}
                                                size="small"
                                            />
                                            <Box sx={{ ml: 1, flex: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    {getPriorityEmoji(assignment.priority)} {assignment.summary}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('DueDate') || 'Due'}: {formatDate(assignment.dueDate)}
                                                </Typography>
                                                {assignment.description && (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            display: 'block',
                                                            mt: 0.5,
                                                            maxHeight: '40px',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis'
                                                        }}
                                                        dangerouslySetInnerHTML={{
                                                            __html: assignment.description.length > 100
                                                                ? assignment.description.substring(0, 100) + '...'
                                                                : assignment.description
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseAssignmentDialog}>
                            {t('Cancel') || 'Cancel'}
                        </Button>
                        <Button
                            onClick={handleInsertAssignments}
                            variant="contained"
                            disabled={selectedAssignments.length === 0}
                        >
                            {t('InsertAssignments') || 'Insert Assignments'} ({selectedAssignments.length})
                        </Button>
                    </DialogActions>
                </Dialog>

                <DialogContent >

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Summary Section */}
                            <Box sx={{
                                mb: 2,
                                p: 1.5,
                                backgroundColor: 'rgba(102, 126, 234, 0.04)',
                                borderRadius: 1.5,
                                border: '1px solid rgba(102, 126, 234, 0.1)'
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    {/* Working Days */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.75,
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        minWidth: 'fit-content'
                                    }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                            {t('WorkingDays') || 'Days'}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: '0.95rem' }}>
                                            {scheduleData.filter(day => day.entries && day.entries.length > 0).length}
                                        </Typography>
                                    </Box>

                                    {/* Total Hours */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.75,
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        minWidth: 'fit-content'
                                    }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                            {t('TotalHours') || 'Total'}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: '0.95rem' }}>
                                            {(() => {
                                                const totalHours = scheduleData.reduce((sum, day) => {
                                                    return sum + calculateCorrectTotalHours(day.entries);
                                                }, 0);
                                                const hours = Math.floor(totalHours);
                                                const minutes = Math.round((totalHours - hours) * 60);
                                                return `${hours}h ${minutes}m`;
                                            })()}
                                        </Typography>
                                    </Box>

                                    {/* Average Hours */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.75,
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        minWidth: 'fit-content'
                                    }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                            {t('AvgHoursPerDay') || 'Avg'}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'warning.main', fontSize: '0.95rem' }}>
                                            {(() => {
                                                const workingDays = scheduleData.filter(day => day.entries && day.entries.length > 0);
                                                if (workingDays.length === 0) return '0h';

                                                const totalHours = scheduleData.reduce((sum, day) => {
                                                    return sum + calculateCorrectTotalHours(day.entries);
                                                }, 0);

                                                const avgHours = totalHours / workingDays.length;
                                                const hours = Math.floor(avgHours);
                                                const minutes = Math.round((avgHours - hours) * 60);
                                                return `${hours}h ${minutes}m`;
                                            })()}
                                        </Typography>
                                    </Box>

                                    {/* Entries (Pairs) */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.75,
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        minWidth: 'fit-content'
                                    }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                                            {t('TotalEntries') || 'Entries'}:
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'info.main', fontSize: '0.95rem' }}>
                                            {scheduleData.reduce((sum, day) => {
                                                return sum + getPairCount(day.entries);
                                            }, 0)}
                                        </Typography>
                                    </Box>

                                    {/* Check-ins / Check-outs */}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.5,
                                        py: 0.75,
                                        backgroundColor: 'white',
                                        borderRadius: 1,
                                        border: '1px solid rgba(0, 0, 0, 0.08)',
                                        minWidth: 'fit-content'
                                    }}>
                                        <Typography variant="body2" sx={{ color: 'success.main', fontSize: '0.75rem', fontWeight: 600 }}>
                                            ↓ {scheduleData.reduce((sum, day) => {
                                                return sum + (day.entries ? day.entries.filter(entry => entry.checkInTime && !entry.checkOutTime).length : 0);
                                            }, 0)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.disabled', fontSize: '0.75rem' }}>
                                            /
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'error.main', fontSize: '0.75rem', fontWeight: 600 }}>
                                            ↑ {scheduleData.reduce((sum, day) => {
                                                return sum + (day.entries ? day.entries.filter(entry => entry.checkOutTime && !entry.checkInTime).length : 0);
                                            }, 0)}
                                        </Typography>
                                    </Box>

                                    {/* Issues */}
                                    {(() => {
                                        const totalIssues = scheduleData.reduce((sum, day) => {
                                            const issues = getAttendanceIssues(day.entries);
                                            return sum + (issues.missingCheckouts + issues.missingCheckins);
                                        }, 0);

                                        if (totalIssues > 0) {
                                            return (
                                                <Box sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.75,
                                                    px: 1.5,
                                                    py: 0.75,
                                                    backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                                    borderRadius: 1,
                                                    border: '1px solid rgba(255, 152, 0, 0.2)',
                                                    minWidth: 'fit-content'
                                                }}>
                                                    <Typography variant="body2" sx={{ color: 'warning.main', fontSize: '0.75rem' }}>
                                                        ⚠ {t('Issues') || 'Issues'}:
                                                    </Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'warning.main', fontSize: '0.95rem' }}>
                                                        {totalIssues}
                                                    </Typography>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    })()}
                                </Box>
                            </Box>

                            <TableContainer
                                component={Paper}
                                sx={{
                                    height: 'calc(85vh - 200px)',
                                    direction: direction === 'rtl' ? 'rtl' : 'ltr',
                                    borderRadius: 2,
                                    boxShadow: '0 8px 28px rgba(0, 0, 0, 0.08)',
                                    border: '1px solid rgba(0, 0, 0, 0.06)',

                                    width: '100%'
                                }}
                            >
                                <Table
                                    stickyHeader
                                    dir={direction}
                                    sx={{
                                        '& th, & td': {
                                            textAlign: direction === 'rtl' ? 'right' : 'left',
                                            py: 1.5,
                                            fontSize: '0.95rem',
                                            verticalAlign: 'top'
                                        },
                                        '& thead th': {
                                            backgroundColor: '#f3f6fb',
                                            fontWeight: 700,
                                            fontSize: '0.9rem',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 1,
                                            borderBottom: '2px solid #e0e0e0',
                                            textAlign: 'center'
                                        },
                                        '& tbody tr:hover': {
                                            backgroundColor: 'rgba(102, 126, 234, 0.06)'
                                        },
                                        '& tbody td': {
                                            textAlign: 'center',
                                            verticalAlign: 'middle'
                                        }
                                    }}
                                >
                                    <TableHead >
                                        <TableRow >
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('Date') || 'Date'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('FirstSignIn') || 'First Sign In'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('LastSignOut') || 'Last Sign Out'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('TotalHours') || 'Total Hours'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('Entries') || 'Entries'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('Remarks') || 'Remarks'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('LinkedAssignments') || 'Linked Assignments'}
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold' }}>
                                                {t('Actions') || 'Actions'}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody sx={{
                                        '& td': {
                                            textAlign: 'center',
                                            verticalAlign: 'middle'
                                        }
                                    }}>
                                        {paginatedData.map((dayData) => (
                                            <React.Fragment key={dayData.day}>
                                                <TableRow hover>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexDirection: direction !== 'rtl' ? 'row-reverse' : 'row'
                                                        }}>
                                                            {/** Compute pair count from entries length to treat in+out as one */}
                                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                {formatDate(dayData.date)}
                                                            </Typography>
                                                            {(getPairCount(dayData.entries) > 1) && (
                                                                <Box
                                                                    sx={{
                                                                        width: 8,
                                                                        height: 8,
                                                                        borderRadius: '50%',
                                                                        bgcolor: 'success.main',
                                                                        [direction === 'rtl' ? 'mr' : 'ml']: 1
                                                                    }}
                                                                />
                                                            )}
                                                            {(() => {
                                                                const issues = getAttendanceIssues(dayData.entries);
                                                                if (issues.hasIssues) {
                                                                    return (
                                                                        <Tooltip title={
                                                                            issues.missingCheckouts > 0 && issues.missingCheckins > 0
                                                                                ? `${t('MissingCheckout') || 'Missing checkout'}: ${issues.missingCheckouts}, ${t('MissingCheckin') || 'Missing check-in'}: ${issues.missingCheckins}`
                                                                                : issues.missingCheckouts > 0
                                                                                    ? `${t('MissingCheckout') || 'Missing checkout'}: ${issues.missingCheckouts}`
                                                                                    : `${t('MissingCheckin') || 'Missing check-in'}: ${issues.missingCheckins}`
                                                                        }>
                                                                            <WarningAmberIcon
                                                                                sx={{
                                                                                    [direction === 'rtl' ? 'mr' : 'ml']: 1,
                                                                                    color: 'warning.main',
                                                                                    fontSize: 18
                                                                                }}
                                                                            />
                                                                        </Tooltip>
                                                                    );
                                                                }
                                                                return null;
                                                            })()}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexDirection: direction !== 'rtl' ? 'row-reverse' : 'row'
                                                        }}>
                                                            <TimeIcon sx={{ fontSize: 16, [direction === 'rtl' ? 'ml' : 'mr']: 0.5, color: 'text.secondary' }} />
                                                            <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>
                                                                {formatTime(dayData.firstSignIn)}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            flexDirection: direction !== 'rtl' ? 'row-reverse' : 'row'
                                                        }}>
                                                            <TimeIcon sx={{ fontSize: 16, [direction === 'rtl' ? 'ml' : 'mr']: 0.5, color: 'text.secondary' }} />
                                                            <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>
                                                                {formatTime(dayData.lastSignOut)}
                                                            </Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Chip
                                                            label={formatHours(calculateCorrectTotalHours(dayData.entries))}
                                                            color={calculateCorrectTotalHours(dayData.entries) > 0 ? 'primary' : 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Chip
                                                            label={getPairCount(dayData.entries)}
                                                            color={getPairCount(dayData.entries) > 1 ? 'success' : 'default'}
                                                            size="small"
                                                            variant={getPairCount(dayData.entries) > 0 ? 'filled' : 'outlined'}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        {editingRemarks === dayData.day ? (
                                                            <Box sx={{ minWidth: 300, maxWidth: 500, mx: 'auto' }}>
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    gap: 1,
                                                                    mb: 1,
                                                                    flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
                                                                    justifyContent: 'flex-end'
                                                                }}>
                                                                    <Button
                                                                        size="small"
                                                                        variant="outlined"
                                                                        startIcon={<AddIcon />}
                                                                        onClick={handleOpenAssignmentDialog}
                                                                        sx={{
                                                                            textTransform: 'none',
                                                                            fontSize: '0.75rem',
                                                                            py: 0.5,
                                                                            px: 1,
                                                                            borderColor: '#667eea',
                                                                            color: '#667eea',
                                                                            '&:hover': {
                                                                                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                                                                borderColor: '#667eea'
                                                                            }
                                                                        }}
                                                                    >
                                                                        {t('LinkAssignments') || 'Link Assignments'}
                                                                    </Button>
                                                                </Box>
                                                                <TipTapEditor
                                                                    value={tempRemarks || ''}
                                                                    onChange={setTempRemarks}
                                                                    placeholder={t('EnterDescription') || 'Enter description...'}
                                                                    direction={direction}
                                                                />
                                                            </Box>
                                                        ) : (
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    maxWidth: 500,
                                                                    wordBreak: 'break-word',
                                                                    direction: direction,
                                                                    textAlign: 'center',
                                                                    mx: 'auto'
                                                                }}
                                                                dangerouslySetInnerHTML={{ __html: dayData.remarks || '-' }}
                                                            />
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        {editingRemarks === dayData.day ? (
                                                            // Show editable linked assignments
                                                            linkedAssignments.length > 0 ? (
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 0.5,
                                                                    alignItems: 'center',
                                                                    minWidth: 200
                                                                }}>
                                                                    <Typography variant="caption" sx={{
                                                                        fontWeight: 600,
                                                                        color: 'text.secondary',
                                                                        mb: 0.5
                                                                    }}>
                                                                        {t('LinkedAssignments') || 'Linked Assignments'}
                                                                    </Typography>
                                                                    {linkedAssignments.map((assignment) => (
                                                                        <Box
                                                                            key={assignment.id}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                gap: 0.25,
                                                                                p: 0.75,
                                                                                borderRadius: 1,
                                                                                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                                                                border: '1px solid rgba(102, 126, 234, 0.3)',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s',
                                                                                position: 'relative',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'rgba(102, 126, 234, 0.2)',
                                                                                    borderColor: '#667eea'
                                                                                }
                                                                            }}
                                                                            onClick={() => handleAssignmentClick(assignment.id)}
                                                                        >
                                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                                                                <Typography variant="caption" sx={{
                                                                                    color: '#667eea',
                                                                                    fontWeight: 600,
                                                                                    fontSize: '0.75rem'
                                                                                }}>
                                                                                    {getPriorityEmoji(assignment.priority)} {assignment.summary}
                                                                                </Typography>
                                                                                <IconButton
                                                                                    size="small"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        handleRemoveLinkedAssignment(assignment.id);
                                                                                    }}
                                                                                    sx={{
                                                                                        p: 0.25,
                                                                                        '&:hover': {
                                                                                            backgroundColor: 'rgba(255, 0, 0, 0.1)'
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <CloseIcon sx={{ fontSize: 12, color: 'error.main' }} />
                                                                                </IconButton>
                                                                            </Box>
                                                                            {assignment.description && (
                                                                                <Typography variant="caption" sx={{
                                                                                    color: 'text.secondary',
                                                                                    fontSize: '0.65rem',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    display: '-webkit-box',
                                                                                    WebkitLineClamp: 2,
                                                                                    WebkitBoxOrient: 'vertical',
                                                                                    lineHeight: 1.3
                                                                                }}>
                                                                                    {stripHtmlTags(assignment.description)}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    ))}
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                    -
                                                                </Typography>
                                                            )
                                                        ) : (
                                                            // Show preview of linked assignments when not editing
                                                            dayData.linkedAssignments && dayData.linkedAssignments.length > 0 ? (
                                                                <Box sx={{
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    gap: 0.5,
                                                                    alignItems: 'center',
                                                                    minWidth: 200
                                                                }}>
                                                                    <Typography variant="caption" sx={{
                                                                        fontWeight: 600,
                                                                        color: 'text.secondary',
                                                                        mb: 0.5
                                                                    }}>
                                                                        {t('LinkedAssignments') || 'Linked Assignments'}
                                                                    </Typography>
                                                                    {dayData.linkedAssignments.slice(0, 2).map((assignment) => (
                                                                        <Box
                                                                            key={assignment.id}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                gap: 0.25,
                                                                                p: 0.75,
                                                                                borderRadius: 1,
                                                                                backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                                                                border: '1px solid rgba(102, 126, 234, 0.3)',
                                                                                cursor: 'pointer',
                                                                                transition: 'all 0.2s',
                                                                                width: '100%',
                                                                                '&:hover': {
                                                                                    backgroundColor: 'rgba(102, 126, 234, 0.2)',
                                                                                    borderColor: '#667eea'
                                                                                }
                                                                            }}
                                                                            onClick={() => handleAssignmentClick(assignment.id)}
                                                                        >
                                                                            <Typography variant="caption" sx={{
                                                                                color: '#667eea',
                                                                                fontWeight: 600,
                                                                                fontSize: '0.75rem'
                                                                            }}>
                                                                                {getPriorityEmoji(assignment.priority)} {assignment.summary}
                                                                            </Typography>
                                                                            {assignment.description && (
                                                                                <Typography variant="caption" sx={{
                                                                                    color: 'text.secondary',
                                                                                    fontSize: '0.65rem',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    display: '-webkit-box',
                                                                                    WebkitLineClamp: 1,
                                                                                    WebkitBoxOrient: 'vertical'
                                                                                }}>
                                                                                    {stripHtmlTags(assignment.description)}
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    ))}
                                                                    {dayData.linkedAssignments.length > 2 && (
                                                                        <Typography variant="caption" sx={{
                                                                            color: 'text.secondary',
                                                                            fontSize: '0.6rem'
                                                                        }}>
                                                                            +{dayData.linkedAssignments.length - 2} more
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            ) : (
                                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                                    -
                                                                </Typography>
                                                            )
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: 'center' }}>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            gap: 0.5,
                                                            justifyContent: 'center',
                                                            flexDirection: direction === 'rtl' ? 'row-reverse' : 'row'
                                                        }}>
                                                            {editingRemarks === dayData.day ? (
                                                                <>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => saveRemarks(dayData.day)}
                                                                        color="primary"
                                                                    >
                                                                        <SaveIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={cancelEditingRemarks}
                                                                        color="error"
                                                                    >
                                                                        <CancelIcon fontSize="small" />
                                                                    </IconButton>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            startEditingRemarks(dayData.day, dayData.remarks);
                                                                            // Also expand the row to show individual entries
                                                                            if (!expandedRows.has(dayData.day)) {
                                                                                setExpandedRows(prev => new Set([...prev, dayData.day]));
                                                                            }
                                                                        }}
                                                                        color="primary"
                                                                    >
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            toggleRowExpansion(dayData.day);
                                                                            // If expanding and current month is editable, enable edit mode for all entries in this day
                                                                            if (!expandedRows.has(dayData.day) && isCurrentMonthEditable()) {
                                                                                // Find the first entry to start editing
                                                                                const firstEntry = dayData.entries[0];
                                                                                if (firstEntry) {
                                                                                    startEditingEntry(firstEntry);
                                                                                }
                                                                            } else {
                                                                                // If collapsing, cancel any editing
                                                                                cancelEditingEntry();
                                                                            }
                                                                        }}
                                                                        color="primary"
                                                                    >
                                                                        {expandedRows.has(dayData.day) ?
                                                                            <ExpandLessIcon fontSize="small" /> :
                                                                            <ExpandMoreIcon fontSize="small" />
                                                                        }
                                                                    </IconButton>
                                                                </>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>

                                                {/* Collapsible row for individual entries */}
                                                <TableRow>
                                                    <TableCell colSpan={7} sx={{ py: 0 }}>
                                                        <Collapse in={expandedRows.has(dayData.day)} timeout="auto" unmountOnExit>
                                                            <Box sx={{ p: 2, bgcolor: 'grey.50', pl: direction === 'rtl' ? 4 : 2 }}>
                                                                {(() => {
                                                                    const issues = getAttendanceIssues(dayData.entries);
                                                                    if (issues.hasIssues) {
                                                                        return (
                                                                            <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ mb: 1 }}>
                                                                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
                                                                                    <Typography variant="body2">
                                                                                        {issues.missingCheckouts > 0 && issues.missingCheckins > 0
                                                                                            ? `${t('MissingCheckout') || 'Missing checkout'}: ${issues.missingCheckouts}, ${t('MissingCheckin') || 'Missing check-in'}: ${issues.missingCheckins}`
                                                                                            : issues.missingCheckouts > 0
                                                                                                ? `${t('MissingCheckout') || 'Missing checkout'}: ${issues.missingCheckouts}`
                                                                                                : `${t('MissingCheckin') || 'Missing check-in'}: ${issues.missingCheckins}`
                                                                                        }
                                                                                    </Typography>
                                                                                </Box>
                                                                            </Alert>
                                                                        );
                                                                    }
                                                                    return null;
                                                                })()}
                                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
                                                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', textAlign: direction !== 'rtl' ? 'right' : 'left' }}>
                                                                        {t('IndividualEntries') || 'Individual Entries'}
                                                                    </Typography>
                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            startIcon={<AddIcon />}
                                                                            onClick={() => {
                                                                                // Add check-in for this day
                                                                                const date = dayjs(dayData.date);
                                                                                setNewAttendanceDate(date.format('YYYY-MM-DD'));
                                                                                setNewAttendanceCheckIn(dayjs().format('HH:mm'));
                                                                                setNewAttendanceCheckOut('');
                                                                                setNewAttendanceRemarks('');
                                                                                setShowAddAttendance(true);
                                                                            }}
                                                                            sx={{
                                                                                textTransform: 'none',
                                                                                fontSize: '0.75rem',
                                                                                py: 0.5,
                                                                                px: 1
                                                                            }}
                                                                        >
                                                                            {t('AddCheckIn') || 'Add Check-In'}
                                                                        </Button>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            startIcon={<AddIcon />}
                                                                            onClick={() => {
                                                                                // Add check-out for this day
                                                                                const date = dayjs(dayData.date);
                                                                                setNewAttendanceDate(date.format('YYYY-MM-DD'));
                                                                                setNewAttendanceCheckIn('');
                                                                                setNewAttendanceCheckOut(dayjs().format('HH:mm'));
                                                                                setNewAttendanceRemarks('');
                                                                                setShowAddAttendance(true);
                                                                            }}
                                                                            sx={{
                                                                                textTransform: 'none',
                                                                                fontSize: '0.75rem',
                                                                                py: 0.5,
                                                                                px: 1
                                                                            }}
                                                                        >
                                                                            {t('AddCheckOut') || 'Add Check-Out'}
                                                                        </Button>
                                                                    </Box>
                                                                </Box>
                                                                <Table
                                                                    size="small"
                                                                    dir={direction}
                                                                    sx={{
                                                                        '& th, & td': {
                                                                            textAlign: direction === 'rtl' ? 'right' : 'left',
                                                                            py: 0.5,
                                                                            fontSize: '0.875rem'
                                                                        },
                                                                        '& thead th': {
                                                                            backgroundColor: '#f8f9fb'
                                                                        },
                                                                        '& tbody tr:nth-of-type(odd)': {
                                                                            backgroundColor: 'rgba(0,0,0,0.015)'
                                                                        }
                                                                    }}
                                                                >
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell
                                                                                sx={{
                                                                                    cursor: 'pointer',
                                                                                    userSelect: 'none',
                                                                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                                                }}
                                                                                onClick={() => handleSort('checkInTime')}
                                                                            >
                                                                                <Box sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 0.5,
                                                                                    flexDirection: direction === 'rtl' ? 'row-reverse' : 'row'
                                                                                }}>
                                                                                    {t('SignIn') || 'Sign In'}
                                                                                    {sortField === 'checkInTime' && (
                                                                                        sortOrder === 'asc' ?
                                                                                            <ArrowUpwardIcon fontSize="small" /> :
                                                                                            <ArrowDownwardIcon fontSize="small" />
                                                                                    )}
                                                                                </Box>
                                                                            </TableCell>
                                                                            <TableCell
                                                                                sx={{
                                                                                    cursor: 'pointer',
                                                                                    userSelect: 'none',
                                                                                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                                                                                }}
                                                                                onClick={() => handleSort('checkOutTime')}
                                                                            >
                                                                                <Box sx={{
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    gap: 0.5,
                                                                                    flexDirection: direction === 'rtl' ? 'row-reverse' : 'row'
                                                                                }}>
                                                                                    {t('SignOut') || 'Sign Out'}
                                                                                    {sortField === 'checkOutTime' && (
                                                                                        sortOrder === 'asc' ?
                                                                                            <ArrowUpwardIcon fontSize="small" /> :
                                                                                            <ArrowDownwardIcon fontSize="small" />
                                                                                    )}
                                                                                </Box>
                                                                            </TableCell>
                                                                            <TableCell>{t('Actions') || 'Actions'}</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {sortEntries(dayData.entries).map((entry, index) => {
                                                                            return (
                                                                                <TableRow key={entry.id}>
                                                                                    <TableCell>
                                                                                        {editingEntry === entry.id && entry.checkInTime ? (
                                                                                            <TextField
                                                                                                value={tempCheckInTime}
                                                                                                onChange={(e) => setTempCheckInTime(e.target.value)}
                                                                                                size="small"
                                                                                                type="time"
                                                                                                sx={{ minWidth: 100 }}
                                                                                                dir={direction}
                                                                                            />
                                                                                        ) : (
                                                                                            <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>
                                                                                                {formatTime(entry.checkInTime)}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        {editingEntry === entry.id && entry.checkOutTime ? (
                                                                                            <TextField
                                                                                                value={tempCheckOutTime}
                                                                                                onChange={(e) => setTempCheckOutTime(e.target.value)}
                                                                                                size="small"
                                                                                                type="time"
                                                                                                sx={{ minWidth: 100 }}
                                                                                                dir={direction}
                                                                                            />
                                                                                        ) : (
                                                                                            <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>
                                                                                                {formatTime(entry.checkOutTime)}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Box sx={{ display: 'flex', gap: 0.5, flexDirection: direction === 'rtl' ? 'row-reverse' : 'row' }}>
                                                                                            {editingEntry === entry.id ? (
                                                                                                <>
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={() => saveEntry(entry.id)}
                                                                                                        color="primary"
                                                                                                    >
                                                                                                        <SaveIcon fontSize="small" />
                                                                                                    </IconButton>
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={cancelEditingEntry}
                                                                                                        color="error"
                                                                                                    >
                                                                                                        <CancelIcon fontSize="small" />
                                                                                                    </IconButton>
                                                                                                </>
                                                                                            ) : (
                                                                                                <>
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={() => startEditingEntry(entry)}
                                                                                                        color="primary"
                                                                                                        title="Edit Entry"
                                                                                                    >
                                                                                                        <EditIcon fontSize="small" />
                                                                                                    </IconButton>
                                                                                                    <IconButton
                                                                                                        size="small"
                                                                                                        onClick={() => handleDeleteEntry(entry.id)}
                                                                                                        color="error"
                                                                                                        title={t('Delete') || 'Delete'}
                                                                                                    >
                                                                                                        <DeleteIcon fontSize="small" />
                                                                                                    </IconButton>
                                                                                                </>
                                                                                            )}
                                                                                        </Box>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            );
                                                                        })}
                                                                    </TableBody>
                                                                </Table>
                                                                <ManualClientTimeSection
                                                                    authToken={authToken}
                                                                    year={currentYear}
                                                                    month={currentMonth}
                                                                    day={dayData.day}
                                                                    defaultUserId={userId || currentUserDetails?.userId}
                                                                    defaultUserType={userType || currentUserDetails?.userType}
                                                                    defaultUserName={currentUserDetails?.name}
                                                                    userOptions={subAccountants}
                                                                    caseOptions={caseIdOptions}
                                                                    direction={direction}
                                                                />
                                                            </Box>
                                                        </Collapse>
                                                    </TableCell>
                                                </TableRow>
                                            </React.Fragment>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                rowsPerPageOptions={[30, 60, 90]}
                                component="div"
                                count={scheduleData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                labelRowsPerPage={t('RowsPerPage') || 'Rows per page'}
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} ${t('of') || 'of'} ${count}`
                                }
                                sx={{
                                    borderTop: '1px solid rgba(0, 0, 0, 0.05)',
                                    background: 'rgba(248, 250, 252, 0.8)',
                                    backdropFilter: 'blur(10px)',
                                    '& .MuiTablePagination-toolbar': {
                                        paddingLeft: direction === 'rtl' ? 0 : 2,
                                        paddingRight: direction === 'rtl' ? 2 : 0,
                                    }
                                }}
                            />
                        </>
                    )}

                </DialogContent>

                <DialogActions sx={{
                    flexDirection: direction === 'rtl' ? 'row-reverse' : 'row',
                    px: 3,
                    py: 2,
                    background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
                    borderTop: '1px solid rgba(0, 0, 0, 0.05)'
                }}>
                    <Button
                        onClick={onClose}
                        variant="contained"
                        dir={direction}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: '0 4px 16px rgba(102, 126, 234, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                                boxShadow: '0 6px 20px rgba(102, 126, 234, 0.4)',
                            }
                        }}
                    >
                        {t('Close') || 'Close'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* DraggableModal for file viewing */}
            <DraggableModal
                bigIframeModalOpen={bigIframeModalOpen}
                handleCloseIframeModal={handleCloseModal}
                openedIframeUrl={openedIframeUrl}
            />

            {/* Assignment Modal */}
            {selectedAssignment && (
                <AssignmentDialog
                    /* Dialog Control */
                    open={assignmentModalOpen}
                    onClose={handleCloseAssignmentModal}
                    dialogOpen={assignmentModalOpen}

                    /* Modes */
                    isEditMode={false}
                    isAddMode={false}
                    isViewOnly={true}

                    /* Assignment data + setter */
                    currentAssignment={selectedAssignment}
                    setCurrentAssignment={setSelectedAssignment}

                    /* Arrays/Data */
                    subAccountants={subAccountants}
                    categoryOptions={categoryOptions}
                    caseIdOptions={caseIdOptions}

                    /* Parent Handlers */
                    handleSaveAssignment={() => { }}
                    handleEditAssignment={() => { }}
                    handleDeleteAssignment={() => { }}
                    handleCloseDialog={handleCloseAssignmentModal}
                    setIsConfirmModalVisable={() => { }}
                    handleOpenModal={handleOpenModal}
                    handleOpenNewCategoryModal={() => { }}
                    changeOnAssignmentCategory={() => { }}
                    changeOnAssignment={() => { }}

                    /* File & Comment Handlers */
                    handleDropDocumentToAssignment={handleDropDocumentToAssignment}
                    handleFileChange={handleFileChange}
                    handleRemoveDocumentFromAssignment={handleRemoveDocumentFromAssignment}
                    handleDropDocumentToComment={handleDropDocumentToComment}
                    handleCommentFileChange={handleCommentFileChange}
                    handleRemoveDocumentFromComment={handleRemoveDocumentFromComment}
                    handleUploadClick={handleUploadClick}
                    fileInputRefUpload={fileInputRefUpload}
                    handleUploadClickComment={handleUploadClickComment}
                    fileInputRefComment={fileInputRefComment}

                    /* Description & Comments */
                    handleChangeCurrentDescription={handleChangeCurrentDescription}
                    handleChangeNewCommentContent={handleChangeNewCommentContent}
                    newComment={newComment}
                    handleAddComment={() => { }}
                    handleDeleteComment={() => { }}

                    /* Helpers */
                    formatDate={formatDate}
                    formatDateWithHour={formatDateWithHour}

                    /* Auth/User */
                    user={{}}

                    /* i18n */
                    t={t}
                    direction={direction}
                />
            )}
        </>
    );
}
